import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { CreditCard, Loader2, CheckCircle, AlertCircle, Shield, ChevronDown, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Initialize Stripe with publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

/**
 * PaymentForm - The actual form that collects card details
 */
const PaymentForm = ({ 
  job, 
  clientSecret, 
  onSuccess, 
  onError,
  savedCards,
  selectedCardId,
  setSelectedCardId,
  useNewCard,
  setUseNewCard,
  saveCard,
  setSaveCard
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const getCurrencySymbol = (currency) => {
    const symbols = { 'GBP': '£', 'USD': '$', 'EUR': '€' };
    return symbols[currency] || currency;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      let result;

      if (useNewCard || savedCards.length === 0) {
        // Use PaymentElement for new card
        result = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/jobs/${job.id}?payment=processing`,
          },
          redirect: 'if_required'
        });
      } else {
        // Use saved card
        result = await stripe.confirmPayment({
          clientSecret,
          confirmParams: {
            payment_method: selectedCardId,
            return_url: `${window.location.origin}/jobs/${job.id}?payment=processing`,
          },
          redirect: 'if_required'
        });
      }

      if (result.error) {
        // Handle 3DS or other errors
        if (result.error.type === 'card_error' || result.error.type === 'validation_error') {
          setError(result.error.message);
          toast.error(result.error.message);
        } else {
          setError('An unexpected error occurred.');
          toast.error('Payment failed. Please try again.');
        }
        onError?.(result.error);
      } else {
        // Payment succeeded
        onSuccess?.(result.paymentIntent);
      }
    } catch (err) {
      setError('Payment failed. Please try again.');
      onError?.(err);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Saved Cards Selection */}
      {savedCards.length > 0 && (
        <div className="space-y-3">
          <label className="text-sm font-medium text-neutral-700">Payment Method</label>
          <Select
            value={useNewCard ? 'new' : selectedCardId}
            onValueChange={(value) => {
              if (value === 'new') {
                setUseNewCard(true);
              } else {
                setUseNewCard(false);
                setSelectedCardId(value);
              }
            }}
          >
            <SelectTrigger className="w-full h-14 bg-white border-neutral-200">
              <SelectValue placeholder="Select payment method" />
            </SelectTrigger>
            <SelectContent>
              {savedCards.map((card) => (
                <SelectItem key={card.id} value={card.id}>
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-4 h-4 text-neutral-500" />
                    <span className="font-medium">•••• {card.last4}</span>
                    <span className="text-neutral-500 capitalize">{card.brand}</span>
                    <span className="text-neutral-400">exp {card.exp_month}/{card.exp_year}</span>
                  </div>
                </SelectItem>
              ))}
              <SelectItem value="new">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-4 h-4 text-blue-500" />
                  <span className="font-medium text-blue-600">Add new card</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Payment Element - Show for new card */}
      {(useNewCard || savedCards.length === 0) && (
        <div className="space-y-4">
          <div className="p-4 bg-neutral-50 rounded-xl border border-neutral-200">
            <PaymentElement 
              options={{
                layout: 'tabs',
                paymentMethodOrder: ['card'],
                fields: {
                  billingDetails: {
                    name: 'auto',
                    email: 'auto',
                    address: {
                      country: 'auto',
                      postalCode: 'auto'
                    }
                  }
                },
                wallets: {
                  applePay: 'auto',
                  googlePay: 'auto'
                }
              }}
            />
          </div>

          {/* Save Card Checkbox */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={saveCard}
              onChange={(e) => setSaveCard(e.target.checked)}
              className="w-5 h-5 rounded border-neutral-300 text-black focus:ring-black"
            />
            <span className="text-sm text-neutral-600">Save this card for future payments</span>
          </label>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Price Summary */}
      <div className="bg-neutral-100 rounded-xl p-4">
        <div className="flex justify-between items-center">
          <span className="text-neutral-600">Total amount</span>
          <span className="text-2xl font-bold">
            {getCurrencySymbol(job.currency)}{job.estimated_price.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={!stripe || processing}
        className="w-full h-14 bg-black hover:bg-neutral-800 text-white text-lg rounded-xl"
        data-testid="pay-confirm-btn"
      >
        {processing ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <Shield className="w-5 h-5 mr-2" />
            Pay {getCurrencySymbol(job.currency)}{job.estimated_price.toFixed(2)}
          </>
        )}
      </Button>

      {/* Security Note */}
      <p className="text-xs text-center text-neutral-500">
        <Shield className="w-3 h-3 inline mr-1" />
        Secured by Stripe. Your card details are encrypted and never stored on our servers.
      </p>
    </form>
  );
};

/**
 * EmbeddedPayment - Main component that handles payment flow
 */
const EmbeddedPayment = ({ 
  job, 
  open, 
  onOpenChange, 
  onPaymentSuccess 
}) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [useNewCard, setUseNewCard] = useState(false);
  const [saveCard, setSaveCard] = useState(true);

  useEffect(() => {
    if (open && job) {
      initializePayment();
    }
  }, [open, job]);

  const initializePayment = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch saved cards first
      const cardsResponse = await axios.get(`${API_URL}/payments/saved-cards`);
      const cards = cardsResponse.data?.cards || [];
      setSavedCards(cards);
      
      if (cards.length > 0) {
        setSelectedCardId(cards[0].id);
        setUseNewCard(false);
      } else {
        setUseNewCard(true);
      }

      // Create payment intent
      const response = await axios.post(`${API_URL}/payments/create-intent`, null, {
        params: { 
          job_id: job.id,
          save_card: saveCard
        }
      });

      setClientSecret(response.data.client_secret);
      setPaymentIntentId(response.data.payment_intent_id);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to initialize payment');
      toast.error('Failed to initialize payment');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    setSuccess(true);
    toast.success('Payment successful!');
    
    // Wait a moment for the success state to show
    setTimeout(() => {
      onPaymentSuccess?.(paymentIntent);
      onOpenChange(false);
    }, 1500);
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
  };

  const getCurrencySymbol = (currency) => {
    const symbols = { 'GBP': '£', 'USD': '$', 'EUR': '€' };
    return symbols[currency] || currency;
  };

  const appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#000000',
      colorBackground: '#ffffff',
      colorText: '#1a1a1a',
      colorDanger: '#ef4444',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      borderRadius: '12px',
      spacingUnit: '4px'
    },
    rules: {
      '.Input': {
        border: '1px solid #e5e5e5',
        padding: '14px 16px',
        fontSize: '16px'
      },
      '.Input:focus': {
        border: '2px solid #000000',
        boxShadow: 'none'
      },
      '.Label': {
        fontWeight: '500',
        marginBottom: '8px'
      },
      '.Tab': {
        border: '1px solid #e5e5e5',
        borderRadius: '8px'
      },
      '.Tab--selected': {
        backgroundColor: '#000000',
        color: '#ffffff'
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Payment
          </DialogTitle>
        </DialogHeader>

        {/* Success State */}
        {success && (
          <div className="py-12 text-center">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-green-700 mb-2">Payment Successful!</h3>
            <p className="text-neutral-600">Searching for contractors...</p>
          </div>
        )}

        {/* Loading State */}
        {loading && !success && (
          <div className="py-12 text-center">
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-neutral-400" />
            <p className="text-neutral-600">Initializing payment...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && !success && (
          <div className="py-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={initializePayment} variant="outline">
              Try Again
            </Button>
          </div>
        )}

        {/* Payment Form */}
        {clientSecret && !loading && !success && (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance,
              loader: 'auto'
            }}
          >
            <PaymentForm
              job={job}
              clientSecret={clientSecret}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              savedCards={savedCards}
              selectedCardId={selectedCardId}
              setSelectedCardId={setSelectedCardId}
              useNewCard={useNewCard}
              setUseNewCard={setUseNewCard}
              saveCard={saveCard}
              setSaveCard={setSaveCard}
            />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmbeddedPayment;
